import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { toast } from 'react-toastify'
import Select from 'react-select'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import { FaSave, FaSpinner, FaCheck, FaTimes, FaEye, FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import '../../HR Manager/screens/EmployeeGoals.css'
import API_BASE_URL from '../../../config/api'

const HODEmployeeGoals = () => {
  const baseUrl = API_BASE_URL
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation();
  const employeeId = location.state?.employeeId || id; // fallback to URL param
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingEmployee, setFetchingEmployee] = useState(true)
  const [goals, setGoals] = useState([])
  const [employee, setEmployee] = useState(null)
  const [expandedTexts, setExpandedTexts] = useState({})
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedGoal, setSelectedGoal] = useState(null)
  const [approvalLoading, setApprovalLoading] = useState(null); // goal id or null
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectGoal, setRejectGoal] = useState(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectError, setRejectError] = useState(null);

  // Filtering states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedKRA, setSelectedKRA] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [availableKRAs, setAvailableKRAs] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);

  const handleViewGoal = (goal) => {
    setSelectedGoal(goal)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedGoal(null)
  }

  const toggleTextExpansion = (goalId, field) => {
    setExpandedTexts(prev => ({
      ...prev,
      [`${goalId}-${field}`]: !prev[`${goalId}-${field}`]
    }))
  }

  const renderTruncatedText = (text, goalId, field, maxLength = 15) => {
    if (!text || text.length <= maxLength) return text

    const key = `${goalId || 'unknown'}-${field}`
    const isExpanded = expandedTexts[key]

    return (
      <span>
        {isExpanded ? text : `${text.substring(0, 10)}...`}
        <span
          className="expand-dots"
          onClick={() => toggleTextExpansion(goalId || 'unknown', field)}
          title={isExpanded ? "Click to collapse" : "Click to expand"}
        >
          {isExpanded ? " [collapse]" : ""}
        </span>
      </span>
    )
  }

  const handleApproveGoal = async (goal) => {
    setApprovalLoading(goal.id);
    try {
      const token = Cookies.get('token');
      await axios.post(`${baseUrl}/api/goals/${goal.id}/approval/`, { action: 'approve' }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      toast.success('Goal approved!');
      // Refresh goals
      const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const emp = response.data.employee;
      setEmployee(emp);
      setGoals(emp.goals || []);
    } catch (err) {
      console.log(err)
      toast.error(err.message || 'Failed to approve goal');
    } finally {
      setApprovalLoading(null);
    }
  };
  const openRejectModal = (goal) => {
    setRejectGoal(goal);
    setRejectReason('');
    setRejectError(null);
    setRejectModalOpen(true);
  };
  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setRejectGoal(null);
    setRejectReason('');
    setRejectError(null);
  };
  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    setRejectSubmitting(true);
    setRejectError(null);
    try {
      const token = Cookies.get('token');
      await axios.post(`${baseUrl}/api/goals/${rejectGoal.id}/approval/`, { action: 'reject', reason: rejectReason }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      toast.success('Goal rejected!');
      // Refresh goals
      const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const emp = response.data.employee;
      setEmployee(emp);
      setGoals(emp.goals || []);
      closeRejectModal();
    } catch (err) {

      setRejectError(err.message || 'Failed to reject goal');
    } finally {
      setRejectSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      setFetchingEmployee(true)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        console.log(response.data.employee)
        const emp = response.data.employee
        setEmployee(emp)
        const allGoals = emp.goals || []
        setGoals(allGoals)

        // Extract available years from goals
        const years = Array.from(new Set(allGoals.map(g => g.year).filter(y => y !== undefined && y !== null))).sort((a, b) => b - a)
        setAvailableYears(years)

        // Set default year if current year is not available
        if (years.length > 0 && !years.includes(selectedYear)) {
          setSelectedYear(years[0])
        }
      } catch (err) {
        toast.error('Failed to fetch employee data.')
      } finally {
        setFetchingEmployee(false)
      }
    }
    if (employeeId) fetchEmployee()
  }, [employeeId])

  // Filter goals by selected year
  useEffect(() => {
    if (goals.length > 0) {
      const filtered = goals.filter(goal => {
        if (goal.year !== undefined && goal.year !== null) {
          return goal.year === selectedYear
        }
        // Fallback to date-based filtering if year field is not available
        const goalDate = new Date(goal.created_at || goal.updated_at)
        return goalDate.getFullYear() === selectedYear
      })
      setFilteredGoals(filtered)
    } else {
      setFilteredGoals([])
    }
  }, [goals, selectedYear])

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
        <div className="page-header">
          <div className="header-content" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            gap: 16,
            minHeight: '60px'
          }}>
            <div className="page-title" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              flex: '1',
              minWidth: 0
            }}>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>Employee KPIs</h1>
              {employee && (
                <span style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#4F46E5',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {employee.first_name} {employee.last_name}
                </span>
              )}
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexShrink: 0
            }}>
              {availableYears.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{
                    fontSize: 13,
                    color: '#64748b',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}>Year:</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      background: '#fff',
                      color: '#374151',
                      fontSize: 13,
                      cursor: 'pointer',
                      fontWeight: '500',
                      minWidth: '80px',
                      maxWidth: '100px'
                    }}
                  >
                    {availableYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                style={{
                  background: '#4F46E5',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 16px',
                  fontWeight: '600',
                  fontSize: 14,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  transition: 'background 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => navigate('/hod/employees')}
              >
                Back to Employees
              </button>
            </div>
          </div>
        </div>
        <div className="hr-content-wrapper">
          {fetchingEmployee ? (
            <div className="loading-state"><FaSpinner className="spinner" /> Loading employee data...</div>
          ) : employee ? (
            <>
              {filteredGoals.length > 0 && (
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ fontSize: 14, color: '#64748b' }}>
                    Showing <strong>{filteredGoals.length}</strong> KPIs for <strong>{selectedYear}</strong>
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    Total: {goals.length} KPIs across all years
                  </div>
                </div>
              )}
              <div className="goals-table-wrapper">
                <table className="goals-table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Stakeholder</th>
                      <th>RAG</th>
                      <th>Quarter</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGoals.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="empty-row">
                          No KPIs found for this employee for {selectedYear}.
                        </td>
                      </tr>
                    ) : (
                      (() => {
                        // Group filtered goals by KRA, similar to other KPI screens
                        const groupedByKra = filteredGoals.reduce((acc, goal) => {
                          const kraName =
                            (goal.key_result_area && typeof goal.key_result_area === 'object'
                              ? goal.key_result_area.name
                              : goal.key_result_area || goal.name) || 'Uncategorized';
                          if (!acc[kraName]) acc[kraName] = [];
                          acc[kraName].push(goal);
                          return acc;
                        }, {});

                        return Object.entries(groupedByKra).map(([kraName, kraGoals]) => (
                          <React.Fragment key={kraName}>
                            <tr className="kra-group-row">
                              <td colSpan={6} style={{ fontWeight: 700, background: '#f1f5f9' }}>
                                {kraName}
                              </td>
                            </tr>
                            {kraGoals.map((goal, idx) => (
                              <tr key={goal.id || `${kraName}-${idx}`}>
                                <td>
                                  {renderTruncatedText(
                                    goal.activity,
                                    goal.id || `${kraName}-${idx}`,
                                    'activity'
                                  )}
                                </td>
                                <td>{goal.stakeholder}</td>
                                <td>
                                  <div className="rag-bar-bg">
                                    <div
                                      className="rag-bar-fill"
                                      style={{ backgroundColor: goal.progress_indicator || '#e5e7eb', width: '100%' }}
                                    >
                                      <span className="rag-bar-label">{goal.rag_color || ''}</span>
                                    </div>
                                  </div>
                                </td>
                                <td>{goal.quarter}</td>
                                <td>
                                  <span className={`status-badge status-${goal.approval_status?.toLowerCase() || 'unknown'}`}>
                                    {goal.approval_status === 'pending' && <FaSpinner className="pending-spinner" />}
                                    {goal.approval_status
                                      ? goal.approval_status.charAt(0).toUpperCase() + goal.approval_status.slice(1)
                                      : ''}
                                  </span>
                                </td>
                                <td>
                                  <div className="goal-actions">
                                    <button
                                      className="action-button view-button"
                                      title="View"
                                      onClick={() => handleViewGoal(goal)}
                                    >
                                      <FaEye />
                                    </button>
                                    {goal.approval_status === 'pending' && (
                                      <>
                                        <button
                                          className="action-button approve-button"
                                          title="Approve"
                                          onClick={() => handleApproveGoal(goal)}
                                          disabled={approvalLoading === goal.id}
                                        >
                                          {approvalLoading === goal.id ? (
                                            <FaSpinner className="pending-spinner" />
                                          ) : (
                                            <FaCheckCircle style={{ color: '#22c55e' }} />
                                          )}
                                        </button>
                                        <button
                                          className="action-button reject-button"
                                          title="Reject"
                                          onClick={() => openRejectModal(goal)}
                                        >
                                          <FaTimesCircle style={{ color: '#ef4444' }} />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ));
                      })()
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="error-state">Employee not found.</div>
          )}
        </div>
      </div>

      {modalOpen && selectedGoal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-content-employee-goal">
            <div className="modal-header">
              <h2>KPI Details</h2>
              <button type="button" onClick={closeModal} className="modal-close-btn"><FaTimes /></button>
            </div>
            <div className="modal-body">
              <div className="goal-details-grid">
                <div className="goal-detail-row">
                  <label>Key Result Area:</label>
                  <span>{selectedGoal.key_result_area?.name || selectedGoal.name}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Activity:</label>
                  <span>{selectedGoal.activity}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Stakeholder:</label>
                  <span>{selectedGoal.stakeholder}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Quarter:</label>
                  <span>{selectedGoal.quarter}</span>
                </div>
                <div className="goal-detail-row">
                  <label>RAG Color:</label>
                  <div className="progress-indicator-display" style={{ backgroundColor: selectedGoal.progress_indicator || '#e5e7eb', width: '100%' }}>

                  </div>
                </div>
                <div className="goal-detail-row">
                  <label>Completion Status:</label>
                  <span>
                    {selectedGoal.status
                      ? selectedGoal.status.charAt(0).toUpperCase() + selectedGoal.status.slice(1).replace(/_/g, ' ')
                      : 'N/A'}
                  </span>
                </div>
                <div className="goal-detail-row">
                  <label>Is Overdue:</label>
                  <span className={selectedGoal.is_overdue ? 'overdue-yes' : 'overdue-no'}>
                    {selectedGoal.is_overdue ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="goal-detail-row">
                  <label>Approval Status:</label>
                  <span style={{ color: '#ffc' }} className={`status-badge status-${selectedGoal.approval_status?.toLowerCase() || 'unknown'}`}>
                    {selectedGoal.approval_status === 'pending' && <FaSpinner className="pending-spinner" />}
                    {selectedGoal.approval_status ? selectedGoal.approval_status.charAt(0).toUpperCase() + selectedGoal.approval_status.slice(1) : 'N/A'}
                  </span>
                </div>
                <div className="goal-detail-row">
                  <label>Due Date:</label>
                  <span>
                    {selectedGoal.completion_date
                      ? new Date(selectedGoal.completion_date).toLocaleDateString('en-GB')
                      : 'N/A'}
                  </span>
                </div>
                {selectedGoal.approval_status === 'rejected' && selectedGoal.rejected_reason && (
                  <div className="goal-detail-row full-width">
                    <label>Rejection Reason:</label>
                    <span className="rejection-reason">{selectedGoal.rejected_reason}</span>
                  </div>
                )}


              </div>
              {selectedGoal.approval_status === 'approved' && (
                <div style={{ margin: '32px 0 0 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ fontWeight: 700, fontSize: 20, color: '#1e293b', margin: 0 }}>Progress History</h3>

                  </div>
                  <div className="goals-table-wrapper" style={{ boxShadow: 'none', border: '1px solid #e2e8f0', padding: 0 }}>
                    <table className="goals-table">
                      <thead>
                        <tr>
                          <th>Comment</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedGoal.progress_history.length === 0 ? (
                          <tr><td colSpan={2} className="empty-row">No progress history.</td></tr>
                        ) : (
                          selectedGoal.progress_history.map((ph, i) => (
                            <tr key={ph.id || i}>
                              <td>{ph.comments || 'Null'}</td>
                              <td style={{ textTransform: 'capitalize' }}>{ph.status}</td>
                              <td>{ph.reported_at ? new Date(ph.reported_at).toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeModal} className="modal-close-button">Close</button>
            </div>
          </div>
        </div>
      )}

      {rejectModalOpen && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeRejectModal()}>
          <div className="modal-content-employee-goal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Reject KPI</h2>
              <button type="button" onClick={closeRejectModal} className="modal-close-btn"><FaTimes /></button>
            </div>
            <form onSubmit={handleRejectSubmit} className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="goal-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                <label style={{ fontWeight: 600, color: '#374151', marginBottom: 2 }}>Rejection Reason <span className="required">*</span></label>
                <textarea
                  name="rejection_reason"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  required
                  className="form-input"
                  placeholder="Enter reason for rejection..."
                  rows={4}
                  disabled={rejectSubmitting}
                  style={{ borderRadius: 8, border: '2px solid #e5e7eb', fontSize: 16, padding: '12px 16px', minWidth: '90%' }}
                />
              </div>
              {rejectError && <div className="error-state">{rejectError}</div>}
              <div className="modal-actions">
                <button
                  type="submit"
                  className="submit-button"
                  style={{ width: '100%', fontWeight: 700, fontSize: 17, borderRadius: 8, padding: '14px 0', marginTop: 8, background: 'linear-gradient(135deg,#ef4444 0%, #f87171 100%)', color: '#fff' }}
                  disabled={rejectSubmitting}
                >
                  {rejectSubmitting ? 'Rejecting...' : 'Reject KPI'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default HODEmployeeGoals
